import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import {
  AdminAlertText,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminFilterDropdownButton,
  AdminFilterMenuItem,
  AdminFormSecondaryAction,
  AdminHeaderAction,
  AdminLinkAction,
  AdminMainText,
  AdminMetadataText,
  AdminMetric,
  AdminMetricDelta,
  AdminPageTitle,
  AdminPanelTitle,
  AdminRowAction,
  AdminRowActionsButton,
  AdminSearchField,
  AdminStatusBadge,
  AdminSupportingText,
  AdminTabButton,
  AdminTableBody,
  AdminTableHeader,
  AdminTableMeta,
  AdminWidgetTitle,
  adminFilterDateInputClasses,
  adminFilterDropdownTriggerClass,
  AdminDetailPaginationFooter,
  adminFormTextareaClasses,
  getAdminNoteAddedByDisplay,
  ADMIN_DETAIL_MAIN_HEADER_CLASS,
  ADMIN_DETAIL_TABLE_CLASS,
  adminDetailTabContentClassName,
  ADMIN_TOOLBAR_CLASS,
} from '../shared/adminUx';

import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import useClickOutside from '../../../Hooks/useClickOutside';

const SEVERITY_BADGE_CLASSES = {
  'Level 1 — Low': 'bg-freshgreen text-white',
  'Level 2 — Medium': 'bg-warmyellow text-white',
  'Level 3 — High': 'bg-alertred text-white',
  'Level 4 — Critical': 'bg-alertred text-white font-bold',
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  return date.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Format datetime for Admin Notes display: "14 Jan 2026 · 10:42 AM"
const formatNoteDateTime = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  const datePart = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const timePart = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${datePart} · ${timePart}`;
};

const ViolationDetail = () => {
  const { violationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const itemFromState = location.state?.item;

  const [activeTab, setActiveTab] = useState('overview');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewMedia, setPreviewMedia] = useState(null);
  
  // History pagination state
  const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
  const itemsPerPage = 6;
  
  // Admin Notes state
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [noteForm, setNoteForm] = useState({
    noteText: ''
  });

  useEffect(() => {
    const loadDetail = async () => {
      setLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 350));
      
      const violation = itemFromState || {
        id: `V#${violationId}`,
        violationId: `V#${violationId}`,
        violationType: 'Document Expiry',
        severity: 'Level 2 — Medium',
        entityType: 'Business',
        entityId: 'BP#0001',
        source: 'System',
        status: 'Verified',
        linkedCaseId: null,
        dateCreated: new Date().toISOString(),
      };

      const now = new Date();
      const firstDetected = new Date(now);
      firstDetected.setDate(firstDetected.getDate() - 5);
      const verifiedOn = new Date(now);
      verifiedOn.setDate(verifiedOn.getDate() - 2);
      const dateCreated = firstDetected;
      
      // Generate hash for consistent images
      const hashId = violationId ? String(violationId).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) : 12345;
      const baseSeed = hashId % 10000;
      
      setDetail({
        ...violation,
        associatedRule: 'RL-001',
        assignedAdmin: 'John Smith',
        summary: 'Business licence expired on 12 Aug 2024. System detected expiry. Verified by Compliance.',
        firstDetected: firstDetected.toISOString(),
        verifiedOn: verifiedOn.toISOString(),
        verifiedBy: 'Sarah Johnson',
        evidenceCount: 3,
        linkedEnforcementAction: null,
        dateCreated: dateCreated.toISOString(),
        // Evidence & Attachments
        evidence: {
          gallery: [
            { url: `https://picsum.photos/seed/${baseSeed}1/640/400`, flagged: false, type: 'screenshot' },
            { url: `https://picsum.photos/seed/${baseSeed}2/640/400`, flagged: true, type: 'screenshot' },
            { url: `https://picsum.photos/seed/${baseSeed}3/640/400`, flagged: false, type: 'document' },
          ],
          attachments: [
            { name: 'licence-document.pdf', url: `https://picsum.photos/seed/${baseSeed}4/800/600`, type: 'document', size: '2.1 MB' },
            { name: 'screenshot-1.png', url: `https://picsum.photos/seed/${baseSeed}5/800/600`, type: 'screenshot', size: '1.2 MB' },
          ],
          reviewText: 'Violation detected in business profile information.',
          messageLogs: 'Message logs showing communication about licence expiry.',
          metadata: { timestamp: formatDateTime(firstDetected.toISOString()), ipAddress: '192.168.1.100' },
        },
        // Violation History
        history: [
          { timestamp: formatDateTime(dateCreated.toISOString()), eventType: 'Created', actor: 'System', details: 'Violation created by rules engine' },
          { timestamp: formatDateTime(new Date(firstDetected.getTime() + 3600000).toISOString()), eventType: 'Verified', actor: 'Admin: S.J.', details: 'Violation verified by compliance team' },
          { timestamp: formatDateTime(new Date(firstDetected.getTime() + 10800000).toISOString()), eventType: 'Action Applied', actor: 'Admin: J.S.', details: 'Enforcement action EA-001 applied' },
        ],
        // Repeat Offence Profile
        repeatOffence: {
          totalViolations: 3,
          violationsLast90Days: 2,
          highestSeverity: 'Level 3 — High',
          mostCommonType: 'Document Expiry',
          suspensionHistory: 0,
          ongoingRestrictions: 'Profile verification required',
        },
        // Escalation & Thresholds
        escalation: {
          triggers: [
            { condition: '3 violations in 30 days', action: 'Escalate to Compliance', status: 'Active' },
            { condition: 'Level 4 severity', action: 'Immediate escalation', status: 'Not Triggered' },
            { condition: '2 document violations', action: 'Lock business profile', status: 'Triggered' },
          ],
          thresholds: [
            { rule: 'Fraud indicators detected', action: 'Escalate to Risk & Safety', applied: true },
          ],
        },
        // Linked Enforcement Actions
        enforcementActions: [
          { actionCode: 'EA-001', actionName: 'Lock Profile', appliedBy: 'Admin: J.S.', appliedOn: formatDateTime(new Date(firstDetected.getTime() + 10800000).toISOString()), outcome: 'Success', notes: 'Profile locked pending licence renewal' },
        ],
        // Admin Notes
        notes: [
          {
            id: 'note-1',
            timestamp: formatDateTime(new Date(firstDetected.getTime() + 14400000).toISOString()),
            author: 'Rachel Brown',
            adminName: 'Moderator — Rachel Brown',
            email: 'admin@tradesposter.com',
            category: 'Compliance',
            content: 'Violation verified. Business has been notified to renew licence within 30 days.',
            outcome: 'Resolved',
            linkedRef: { id: violation.violationId, type: 'violation' }
          },
        ],
      });
      
      setLoading(false);
    };

    loadDetail();
  }, [violationId, itemFromState]);

  const renderOverview = () => {
    if (!detail) return null;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Violation ID</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{detail.violationId}</div>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Violation Type</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{detail.violationType}</div>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Severity Level</AdminDetailFieldLabel>
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${SEVERITY_BADGE_CLASSES[detail.severity] || 'bg-mediumgray text-white'}`}>
              {detail.severity}
            </span>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Entity Type</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{detail.entityType}</div>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Entity ID</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{detail.entityId}</div>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Source</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{detail.source}</div>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Associated Rule</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{detail.associatedRule || '—'}</div>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Linked Case ID</AdminDetailFieldLabel>
            {detail.linkedCaseId ? (
              <button
                onClick={() => navigate(`/admin/moderation/queue/${detail.linkedCaseId.replace('MC#', '')}`)}
                className="text-primaryblue hover:text-offblack text-sm font-medium"
              >
                {detail.linkedCaseId}
              </button>
            ) : (
              <div className="text-sm text-offblack font-medium">—</div>
            )}
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Violation Status</AdminDetailFieldLabel>
            <AdminStatusBadge status={detail.status} label={detail.status} />
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Assigned Admin</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{detail.assignedAdmin || 'Unassigned'}</div>
          </div>
        </div>
    );
  };

  const renderSummary = () => {
    if (!detail) return null;

    return (
        <div className="space-y-4">
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Summary</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{detail.summary}</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <AdminDetailFieldLabel className="mb-1">First Detected</AdminDetailFieldLabel>
              <div className="text-sm text-offblack font-medium">{formatDateTime(detail.firstDetected)}</div>
            </div>
            <div className="space-y-1">
              <AdminDetailFieldLabel className="mb-1">Verified On</AdminDetailFieldLabel>
              <div className="text-sm text-offblack font-medium">{formatDateTime(detail.verifiedOn)}</div>
            </div>
            <div className="space-y-1">
              <AdminDetailFieldLabel className="mb-1">Verified By</AdminDetailFieldLabel>
              <div className="text-sm text-offblack font-medium">{detail.verifiedBy || '—'}</div>
            </div>
            <div className="space-y-1">
              <AdminDetailFieldLabel className="mb-1">Evidence Count</AdminDetailFieldLabel>
              <div className="text-sm text-offblack font-medium">{detail.evidenceCount || '—'}</div>
            </div>
            {detail.linkedEnforcementAction && (
              <div className="space-y-1">
                <AdminDetailFieldLabel className="mb-1">Linked Enforcement Action</AdminDetailFieldLabel>
                <div className="text-sm text-offblack font-medium">{detail.linkedEnforcementAction}</div>
              </div>
            )}
          </div>
        </div>
    );
  };

  const tabs = useMemo(() => [
    { id: 'overview', label: 'Overview' },
    { id: 'summary', label: 'Violation Summary' },
    { id: 'evidence', label: 'Evidence & Attachments' },
    { id: 'history', label: 'Violation History' },
    { id: 'repeat-offence', label: 'Repeat Offence Profile' },
    { id: 'escalation', label: 'Escalation & Thresholds' },
    { id: 'enforcement', label: 'Linked Enforcement Actions' },
    { id: 'audit', label: 'Audit Log' },
    { id: 'notes', label: 'Admin Notes' },
  ], []);

  // Admin Notes handlers
  const openNotesModal = () => {
    setNotesModalOpen(true);
  };

  const closeNotesModal = () => {
    setNotesModalOpen(false);
    setNoteForm({ noteText: '' });
  };

  const handleNoteSave = () => {
    if (!noteForm.noteText || noteForm.noteText.trim() === '') {
      alert('Note is required');
      return;
    }

    const now = new Date();
    const timestamp = formatDateTime(now.toISOString());
    
    // Add new note
    // In real app, get admin name and team from auth context
    const newNote = {
      id: `note-${Date.now()}`,
      timestamp: timestamp,
      createdAt: now.toISOString(),
      author: 'Alex Chen',
      adminName: 'Admin — Alex Chen (Moderation)',
      team: 'Moderation',
      email: 'admin@tradesposter.com',
      content: noteForm.noteText.trim(),
      noteContent: noteForm.noteText.trim()
    };
    
    setDetail(prev => ({
      ...prev,
      notes: [newNote, ...(prev.notes || [])]
    }));
    
    closeNotesModal();
  };

  const getRefLink = (ref) => {
    if (!ref || !ref.id) return null;
    switch (ref.type) {
      case 'violation':
        return `/admin/moderation/violations/violation/${ref.id.replace('V#', '')}`;
      case 'case':
        return `/admin/moderation/queue/${ref.id.replace('MC#', '')}`;
      default:
        return null;
    }
  };

  const getRefDisplay = (ref) => {
    if (!ref || !ref.id) return '—';
    return ref.id;
  };

  const renderEvidence = () => {
    if (!detail || !detail.evidence) return null;
    
    const { evidence } = detail;
    
    return (
      <>
        <div className="space-y-6">
            {/* Screenshots */}
            {evidence.gallery && evidence.gallery.length > 0 && (
              <div className="space-y-4">
                <AdminPanelTitle className="text-offblack">Screenshots</AdminPanelTitle>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {evidence.gallery.filter(item => item.type === 'screenshot').map((item, index) => (
                    <button
                      key={item.url || index}
                      type="button"
                      onClick={() => setPreviewMedia(item)}
                      className="relative rounded-lg overflow-hidden border border-lightgray focus:outline-none focus:ring-2 focus:ring-primaryblue cursor-pointer hover:opacity-90 transition-opacity"
                    >
                      <img
                        src={item.url}
                        alt={`Evidence image ${index + 1}`}
                        className="h-32 w-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/300?text=Image';
                        }}
                      />
                      {item.flagged && (
                        <span className="absolute top-2 right-2 px-2 py-1 rounded-full bg-alertred text-white text-[10px] uppercase tracking-wide">
                          Flagged
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Documents */}
            {evidence.attachments && evidence.attachments.filter(att => att.type === 'document').length > 0 && (
              <div className="space-y-3">
                <AdminPanelTitle className="text-offblack">Documents</AdminPanelTitle>
                <div className="space-y-2">
                  {evidence.attachments
                    .filter(att => att.type === 'document')
                    .map((file, idx) => (
                      <div key={idx} className="border border-lightgray rounded-lg p-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-lightgray rounded flex items-center justify-center">
                            <span className="text-base" aria-hidden>📄</span>
                          </div>
                          <div>
                            <div className="text-sm text-offblack font-medium">{file.name}</div>
                            <AdminMetadataText>{file.size || 'Unknown size'}</AdminMetadataText>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="text-primaryblue hover:text-offblack text-sm font-medium"
                          onClick={() => window.open(file.url, '_blank')}
                        >
                          View
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Screenshots Section */}
            {evidence.attachments && evidence.attachments.filter(att => att.type === 'screenshot').length > 0 && (
              <div className="space-y-3">
                <AdminPanelTitle className="text-offblack">Screenshots</AdminPanelTitle>
                <div className="space-y-2">
                  {evidence.attachments
                    .filter(att => att.type === 'screenshot')
                    .map((file, idx) => (
                      <div key={idx} className="border border-lightgray rounded-lg p-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-lightgray rounded flex items-center justify-center">
                            <span className="text-base" aria-hidden>📷</span>
                          </div>
                          <div>
                            <div className="text-sm text-offblack font-medium">{file.name}</div>
                            <AdminMetadataText>{file.size || 'Unknown size'}</AdminMetadataText>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="text-primaryblue hover:text-offblack text-sm font-medium"
                          onClick={() => window.open(file.url, '_blank')}
                        >
                          View
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Metadata */}
            {evidence.metadata && (
              <div className="space-y-3">
                <AdminPanelTitle className="text-offblack">Metadata</AdminPanelTitle>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(evidence.metadata).map(([key, value]) => (
                    <div key={key} className="space-y-1">
                      <AdminDetailFieldLabel className="mb-1">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">{value || '—'}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
        </div>

        {/* Full Image Viewer Modal */}
        {previewMedia && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4"
            onClick={() => setPreviewMedia(null)}
          >
            <div
              className="relative max-w-6xl w-full max-h-[90vh]"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className="absolute -top-10 right-0 text-white hover:text-primaryblue text-sm font-medium transition-colors"
                onClick={() => setPreviewMedia(null)}
              >
                Close
              </button>
              <div className="relative rounded-lg overflow-hidden bg-white">
                <img 
                  src={previewMedia.url} 
                  alt="Full image view" 
                  className="w-full h-full object-contain max-h-[90vh]" 
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/800?text=Image+Not+Available';
                  }}
                />
                {previewMedia.flagged && (
                  <span className="absolute top-3 right-3 px-3 py-1 rounded-full bg-alertred text-white text-xs uppercase tracking-wide">
                    Flagged
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderHistory = () => {
    if (!detail || !detail.history) return null;
    
    return (
        <div className="space-y-4">
            {/* Timeline Container */}
            <div className="relative">
              <div className="space-y-0">
                {(() => {
                  const historyStartIndex = (historyCurrentPage - 1) * itemsPerPage;
                  const historyEndIndex = historyStartIndex + itemsPerPage;
                  const paginatedHistory = detail.history.slice(historyStartIndex, historyEndIndex);
                  
                  return (
                    <>
                      {paginatedHistory.map((entry, index) => {
                        const isLast = index === paginatedHistory.length - 1;
                        
                        return (
                          <div key={index} className="relative flex items-start">
                            {!isLast && (
                              <div className="absolute left-[1.45rem] top-[18px] w-px bg-lightgray" style={{ height: 'calc(100% + 1.5rem)' }}></div>
                            )}
                            
                            <div className="relative z-10 flex-shrink-0 w-12 h-8 flex items-start justify-center">
                              <div className="w-3 h-3 rounded-full bg-lightgray border-2 border-white mt-0.5 flex-shrink-0"></div>
                            </div>
                            
                            <div className="flex-1 ml-4 pb-6">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                                <div className="flex items-center gap-3">
                                  <AdminMetadataText>
                                    {entry.timestamp}
                                  </AdminMetadataText>
                                </div>
                              </div>
                              
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <AdminMainText className="text-offblack font-semibold">
                                    {entry.eventType}
                                  </AdminMainText>
                                </div>
                                
                                {entry.actor && (
                                  <div className="mt-1">
                                    <AdminSupportingText>
                                      By: {entry.actor}
                                    </AdminSupportingText>
                                  </div>
                                )}
                                
                                {entry.details && (
                                  <div className="mt-1">
                                    <AdminSupportingText>
                                      {entry.details}
                                    </AdminSupportingText>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            </div>
            
            {detail.history && detail.history.length > itemsPerPage && (
              <AdminDetailPaginationFooter
                currentPage={historyCurrentPage}
                totalPages={Math.ceil(detail.history.length / itemsPerPage)}
                onPrev={() => setHistoryCurrentPage((prev) => Math.max(1, prev - 1))}
                onNext={() => setHistoryCurrentPage((prev) =>
                  Math.min(Math.ceil(detail.history.length / itemsPerPage), prev + 1)
                )}
                goToPage={setHistoryCurrentPage}
              />
            )}
        </div>
    );
  };

  const renderRepeatOffence = () => {
    if (!detail || !detail.repeatOffence) return null;
    
    const { repeatOffence } = detail;
    
    return (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Total Violations</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{repeatOffence.totalViolations || '—'}</div>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Violations (Last 90 days)</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{repeatOffence.violationsLast90Days || '—'}</div>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Highest Severity Reached</AdminDetailFieldLabel>
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${SEVERITY_BADGE_CLASSES[repeatOffence.highestSeverity] || 'bg-mediumgray text-white'}`}>
              {repeatOffence.highestSeverity || '—'}
            </span>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Most Common Violation Type</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{repeatOffence.mostCommonType || '—'}</div>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Suspension History</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{repeatOffence.suspensionHistory || 0}</div>
          </div>
          <div className="space-y-1">
            <AdminDetailFieldLabel className="mb-1">Ongoing Restrictions</AdminDetailFieldLabel>
            <div className="text-sm text-offblack font-medium">{repeatOffence.ongoingRestrictions || '—'}</div>
          </div>
        </div>
    );
  };

  const renderEscalation = () => {
    if (!detail || !detail.escalation) return null;
    
    const { escalation } = detail;
    
    return (
        <div className="space-y-6">
          {/* Escalation Triggers */}
          {escalation.triggers && escalation.triggers.length > 0 && (
            <div className="space-y-4">
              <AdminPanelTitle className="text-offblack">Escalation Triggers</AdminPanelTitle>
              <div className="overflow-hidden">
                <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(200px, 2fr) minmax(200px, 2fr) minmax(120px, 1fr)' }}>
                  <div className="flex items-center">
                    <AdminTableHeader>Condition</AdminTableHeader>
                  </div>
                  <div className="flex items-center">
                    <AdminTableHeader>Action</AdminTableHeader>
                  </div>
                  <div className="flex items-center">
                    <AdminTableHeader>Status</AdminTableHeader>
                  </div>
                </div>
                
                {escalation.triggers.map((trigger, idx) => (
                  <div key={idx} className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(200px, 2fr) minmax(200px, 2fr) minmax(120px, 1fr)' }}>
                    <div className="flex items-center">
                      <span className="">{trigger.condition}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="">{trigger.action}</span>
                    </div>
                    <div className="flex items-center">
                      <span className={`px-2 py-1 rounded-full text-xs text-white ${
                        trigger.status === 'Active' || trigger.status === 'Triggered'
                          ? 'bg-freshgreen'
                          : 'bg-mediumgray'
                      }`}>
                        {trigger.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Thresholds */}
          {escalation.thresholds && escalation.thresholds.length > 0 && (
            <div className="space-y-4">
              <AdminPanelTitle className="text-offblack">Applied Thresholds</AdminPanelTitle>
              <div className="space-y-2">
                {escalation.thresholds.map((threshold, idx) => (
                  <div key={idx} className="border border-lightgray rounded-lg p-4">
                    <div className="space-y-2">
                      <div className="text-sm text-offblack font-medium">{threshold.rule}</div>
                      <AdminSupportingText>{threshold.action}</AdminSupportingText>
                      {threshold.applied && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-freshgreen text-white">
                          Applied
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
    );
  };

  const renderEnforcementActions = () => {
    if (!detail || !detail.enforcementActions) return null;
    
    return (
      <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
        <div className="overflow-hidden">
          <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(100px, 1fr) minmax(150px, 1.5fr) minmax(120px, 1.2fr) minmax(140px, 1.4fr) minmax(100px, 1fr) minmax(200px, 2fr)' }}>
            <div className="flex items-center">
              <AdminTableHeader>Action Code</AdminTableHeader>
            </div>
            <div className="flex items-center">
              <AdminTableHeader>Action Name</AdminTableHeader>
            </div>
            <div className="flex items-center">
              <AdminTableHeader>Applied By</AdminTableHeader>
            </div>
            <div className="flex items-center">
              <AdminTableHeader>Applied On</AdminTableHeader>
            </div>
            <div className="flex items-center">
              <AdminTableHeader>Outcome</AdminTableHeader>
            </div>
            <div className="flex items-center">
              <AdminTableHeader>Notes</AdminTableHeader>
            </div>
          </div>
          
          {detail.enforcementActions.length === 0 ? (
            <div className="py-10 text-center">
              <AdminSupportingText>
                No enforcement actions linked to this violation.
              </AdminSupportingText>
            </div>
          ) : (
            detail.enforcementActions.map((action, idx) => (
              <div key={idx} className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(100px, 1fr) minmax(150px, 1.5fr) minmax(120px, 1.2fr) minmax(140px, 1.4fr) minmax(100px, 1fr) minmax(200px, 2fr)' }}>
                <div className="flex items-center">
                  <span className="">{action.actionCode}</span>
                </div>
                <div className="flex items-center">
                  <span className="">{action.actionName}</span>
                </div>
                <div className="flex items-center">
                  <span className="">{action.appliedBy}</span>
                </div>
                <div className="flex items-center">
                  <span className="">{action.appliedOn}</span>
                </div>
                <div className="flex items-center">
                  <span className={`px-2 py-1 rounded-full text-xs text-white ${
                    action.outcome === 'Success' ? 'bg-freshgreen' : 'bg-alertred'
                  }`}>
                    {action.outcome}
                  </span>
                </div>
                <div className="flex items-center min-w-0">
                  <span className=" text-offblack truncate" title={action.notes}>
                    {action.notes || '—'}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </DashboardMainContainer>
    );
  };

  const renderAdminNotes = () => {
    if (!detail) return null;
    
    return (
      <div className="space-y-4">
          <div className={ADMIN_TOOLBAR_CLASS}>
            <div className="flex justify-end items-center w-full">
              <AdminRowAction onClick={() => openNotesModal()}>
                Add note
              </AdminRowAction>
            </div>
          </div>
          
          {/* Notes Table */}
          <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
            {/* Header */}
            <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(200px, 1.5fr) minmax(250px, 2fr) minmax(400px, 4fr)' }}>
              <div className="flex items-center">
                <span className="font-semibold">Created At</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold">Added By</span>
              </div>
              <div className="flex items-center">
                <AdminTableHeader>Note</AdminTableHeader></div>
            </div>
            
            {/* Rows - Sort by date (newest first) */}
            {(() => {
              const sortedNotes = [...(detail.notes || [])].sort((a, b) => {
                const aDate = new Date(a.createdAt || a.timestamp || 0);
                const bDate = new Date(b.createdAt || b.timestamp || 0);
                return bDate - aDate;
              });

              return sortedNotes.length > 0 ? sortedNotes.map((note) => {
                const noteContent = note.noteContent || note.content || '';
                const createdAt = note.createdAt ? formatNoteDateTime(note.createdAt) : 
                                 (note.timestamp ? formatNoteDateTime(new Date(note.timestamp)) : '—');
                
                const addedByDisplay = getAdminNoteAddedByDisplay(note);

                return (
                  <div key={note.id} className="grid py-4 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(200px, 1.5fr) minmax(250px, 2fr) minmax(400px, 4fr)' }}>
                    <div className="flex items-start">
                      <span className="">{createdAt}</span>
                    </div>
                    <div className="flex items-start">
                      <span className="font-medium">{addedByDisplay}</span>
                    </div>
                    <div className="flex items-start">
                      <span className="whitespace-pre-wrap">{noteContent}</span>
                    </div>
                  </div>
                );
              }) : (
                <div className="py-8 text-center">
                  <span className="text-mediumgray">No notes yet</span>
                </div>
              );
            })()}
          </DashboardMainContainer>

          {/* Add Note Modal */}
          {notesModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closeNotesModal}>
              <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                  <AdminPanelTitle className="text-offblack">
                    Add Note
                  </AdminPanelTitle>
                  <button
                    onClick={closeNotesModal}
                    className="text-offblack hover:text-primaryblue"
                  >
                    <i className="fi fi-rr-cross dashboard-icon"></i>
                  </button>
                </div>
                
                <div className="space-y-4">
                  {/* Note - Required */}
                  <div>
                    <AdminDetailFieldLabel className="mb-1.5">
                      Note <span className="text-alertred">*</span>
                    </AdminDetailFieldLabel>
                    <textarea
                      value={noteForm.noteText}
                      onChange={(e) => setNoteForm(prev => ({ ...prev, noteText: e.target.value }))}
                      className={adminFormTextareaClasses()}
                      placeholder="Enter note..."
                      required
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3 pt-4">
                    <AdminFormSecondaryAction type="button" onClick={closeNotesModal}
                      >Cancel</AdminFormSecondaryAction>
                      <AdminHeaderAction
                      onClick={handleNoteSave}
                    >
                      Save Note
                    </AdminHeaderAction>
                  </div>
                </div>
              </div>
            </div>
          )}
      </div>
    );
  };

  const renderAuditLog = () => {
    return (
      <AdminSupportingText className="text-mediumgray">
        Audit log will be displayed here.
      </AdminSupportingText>
    );
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'summary':
        return renderSummary();
      case 'evidence':
        return renderEvidence();
      case 'history':
        return renderHistory();
      case 'repeat-offence':
        return renderRepeatOffence();
      case 'escalation':
        return renderEscalation();
      case 'enforcement':
        return renderEnforcementActions();
      case 'audit':
        return renderAuditLog();
      case 'notes':
        return renderAdminNotes();
      default:
        return renderOverview();
    }
  };

  if (loading || !detail) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminSupportingText>Loading violation details...</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="tp-container-main">
        <div>
        <DashboardMainContainer className={ADMIN_DETAIL_MAIN_HEADER_CLASS}>
          <div className="space-y-4">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              <div className="space-y-2">
                <AdminPanelTitle className="text-offblack">
                  {detail.violationId} — {detail.violationType}
                </AdminPanelTitle>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${SEVERITY_BADGE_CLASSES[detail.severity] || 'bg-mediumgray text-white'}`}>
                    {detail.severity}
                  </span>
                  <AdminStatusBadge status={detail.status} label={detail.status} />
                </div>
              </div>
            </div>
          </div>
        </DashboardMainContainer>

        <DashboardMainContainer className="!bg-white">
          <div className="border-b border-lightgray">
            <div className="flex space-x-8 overflow-x-auto">
              {tabs.map((tab) => (
                <AdminTabButton
                  key={tab.id}
                  active={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </AdminTabButton>
              ))}
            </div>
          </div>

          <div className={adminDetailTabContentClassName(activeTab)}>
            {renderActiveTab()}
          </div>
        </DashboardMainContainer>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ViolationDetail;

